/**
 * Shared mock data for the Rule Editor prototype.
 */

export type AdGroupType = 'AUTO' | 'KEYWORD' | 'TARGET' | 'PHRASE' | 'EXACT' | 'BROAD' | 'PRODUCT'

export type DestinationMatchType = 'broad' | 'phrase' | 'exact' | 'product'

export interface Source {
    id: string
    /** Optional ASIN displayed as a prefix in the secondary line. */
    asin?: string
    campaignName: string
    adGroupName: string
    /** Optional short display label used as the source's bold primary line. */
    displayName?: string
    type: AdGroupType
}

export interface Destination {
    id: string
    campaignName: string
    adGroupName: string
    type: AdGroupType
    /** Which match-type chips are currently selected. */
    matchTypes: DestinationMatchType[]
}

export interface PromotionStructure {
    sources: Source[]
    promote: Destination[]
    negate: Destination[]
}

const defaultSources: Source[] = [
    {
        id: 'src-1',
        asin: 'B07N8SKBLY',
        displayName: 'Close',
        campaignName: 'SPA Close - I Cant I Have Plans In The...',
        adGroupName: 'SPA Close - I Cant I Have Plans In The...',
        type: 'AUTO',
    },
]

const defaultPromote: Destination[] = [
    {
        id: 'dst-promote-1',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'Phrase',
        type: 'PHRASE',
        matchTypes: ['broad'],
    },
    {
        id: 'dst-promote-2',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'gifts for dad',
        type: 'PHRASE',
        matchTypes: ['broad'],
    },
]

export const defaultStructure: PromotionStructure = {
    sources: defaultSources,
    promote: defaultPromote,
    negate: [],
}

export interface AvailableAdGroup {
    id: string
    campaignName: string
    adGroupName: string
    type: AdGroupType
    status?: 'enabled' | 'paused' | 'archived'
    asin?: string
}

export const availableAdGroups: AvailableAdGroup[] = [
    // Campaign: SPA Auto - Gifts
    {
        id: 'ag-1',
        campaignName: 'SPA Auto - Gifts',
        adGroupName: 'SPA Auto - Gifts',
        type: 'AUTO',
        status: 'enabled',
        asin: 'B07N8SKBLY',
    },
    {
        id: 'ag-2',
        campaignName: 'SPA Auto - Gifts',
        adGroupName: 'SPA Loose Match - Curated Picks',
        type: 'AUTO',
        status: 'enabled',
    },
    {
        id: 'ag-3',
        campaignName: 'SPA Auto - Gifts',
        adGroupName: 'SPA Close Match - Bestsellers',
        type: 'AUTO',
        status: 'paused',
        asin: 'B08XYZ1234',
    },
    // Campaign: SPM Manual - Gifts for Dad
    {
        id: 'ag-4',
        campaignName: 'SPM Manual - Gifts for Dad',
        adGroupName: 'SPM Broad - Gifts for Dad',
        type: 'BROAD',
        status: 'enabled',
    },
    {
        id: 'ag-5',
        campaignName: 'SPM Manual - Gifts for Dad',
        adGroupName: 'SPM Phrase - Gifts for Dad',
        type: 'PHRASE',
        status: 'enabled',
    },
    {
        id: 'ag-6',
        campaignName: 'SPM Manual - Gifts for Dad',
        adGroupName: 'SPM Exact - Gifts for Dad',
        type: 'EXACT',
        status: 'enabled',
        asin: 'B09ABC5678',
    },
    {
        id: 'ag-7',
        campaignName: 'SPM Manual - Gifts for Dad',
        adGroupName: 'SPM Exact - High Converters',
        type: 'EXACT',
        status: 'archived',
    },
    // Campaign: SP Product Targeting - Bestsellers
    {
        id: 'ag-8',
        campaignName: 'SP Product Targeting - Bestsellers',
        adGroupName: 'SP Targeting - Bestsellers',
        type: 'PRODUCT',
        status: 'enabled',
        asin: 'B07N8SKBLY',
    },
    {
        id: 'ag-9',
        campaignName: 'SP Product Targeting - Bestsellers',
        adGroupName: 'SP Targeting - Competitor ASINs',
        type: 'PRODUCT',
        status: 'enabled',
    },
    {
        id: 'ag-10',
        campaignName: 'SP Product Targeting - Bestsellers',
        adGroupName: 'SP Targeting - Category Browse',
        type: 'TARGET',
        status: 'paused',
    },
    // Campaign: SPA Manual - Gifts
    {
        id: 'ag-11',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'Phrase',
        type: 'PHRASE',
        status: 'enabled',
    },
    {
        id: 'ag-12',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'gifts for dad',
        type: 'PHRASE',
        status: 'enabled',
        asin: 'B07N8SKBLY',
    },
    {
        id: 'ag-13',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'Broad - General Gifts',
        type: 'BROAD',
        status: 'enabled',
    },
    {
        id: 'ag-14',
        campaignName: 'SPA Manual - Gifts',
        adGroupName: 'Exact - Hot Sellers',
        type: 'EXACT',
        status: 'enabled',
    },
    // Campaign: SPM Discovery - New Launches
    {
        id: 'ag-15',
        campaignName: 'SPM Discovery - New Launches',
        adGroupName: 'SPM Auto - Discovery',
        type: 'AUTO',
        status: 'enabled',
    },
    {
        id: 'ag-16',
        campaignName: 'SPM Discovery - New Launches',
        adGroupName: 'SPM Keyword - Discovery',
        type: 'KEYWORD',
        status: 'enabled',
        asin: 'B0CXYZ9999',
    },
]
